import type { ForecastEntry, GridSnapshot } from "@reagent/sim";
import { useGameStore } from "../../app/gameStore";
import { FORECAST_LABEL } from "../../render/hazardTells";
import { NODE_KIND_LABEL } from "./TileInspector";
import "./forecast-panel.css";

/**
 * `00-pillars.md` §3 Phase 2's Forecast, spec 0018: every limit the coming turn
 * crosses if the player plays nothing more, read straight off
 * `GridSnapshot.forecast` — which the engine computes by dry-running a clone of
 * itself, so this panel cannot drift from what actually fires.
 *
 * It sits beside the Run switch rather than behind a phase of its own: the
 * player looks before committing without a second confirmation click, and the
 * list stays up while they play the card that answers it, so the entry
 * disappearing *is* the confirmation that the fix took.
 *
 * It states facts and never advice — the tick and the unit, never a card to
 * play about it. Each row is read by the eye the same way the board's pins are
 * read: `render/hazardTells.ts` is the one source for both, so the list and the
 * badges can never disagree.
 */
export function ForecastPanel() {
  const snapshot = useGameStore((s) => s.snapshot);

  // Empty outside Play by construction (the engine clears it), but the panel
  // has nothing to say then either — a forecast mid-run is a running commentary
  // on a turn already committed.
  if (snapshot.phase !== "play") return null;

  const entries = [...snapshot.forecast].sort(byTickThenTarget);

  return (
    <div className="forecast">
      <span className="forecast__label">forecast</span>
      {entries.length === 0 ? (
        <span className="forecast__clear">nothing crosses a limit this turn</span>
      ) : (
        <ul className="forecast__list">
          {entries.map((entry) => (
            <li className="forecast__row" key={`${entry.kind}:${entry.targetId}`}>
              <span className="forecast__tick">t{entry.tickInTurn}</span>
              <span className="forecast__kind">{FORECAST_LABEL[entry.kind]}</span>
              <span className="forecast__target">{targetName(snapshot, entry.targetId)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Earliest first; ties broken by target so the order is stable between frames. */
function byTickThenTarget(a: ForecastEntry, b: ForecastEntry): number {
  if (a.tickInTurn !== b.tickInTurn) return a.tickInTurn - b.tickInTurn;
  return a.targetId.localeCompare(b.targetId);
}

/**
 * Names the unit the way the inspector names it, so a row and the plate under
 * the cursor agree. Falls back to the raw id for hardware that has already left
 * the board — a destroyed node is still worth naming in a list that explains
 * why it is about to be destroyed.
 */
function targetName(snapshot: GridSnapshot, targetId: string): string {
  const node = snapshot.nodes.find((n) => n.id === targetId);
  if (node) return `${NODE_KIND_LABEL[node.kind]} ${node.id}`;

  const edge = snapshot.edges.find((e) => e.id === targetId);
  if (edge) return `Pipe ${edge.id}`;

  return targetId;
}
